/**
 * Dev-only mock data — fake how MANY of each entity the client sees, so a design can be checked at
 * 0, 1, 3 and 40 without touching the account. Reached from the brand-mark dev menu (long-press the
 * Symphony logo) → "Mock data".
 *
 * How it works: ONE interception point. Every same-origin data call in the app goes through
 * `apiFetch` (lib/api-client.ts); in a dev build it asks this module first, and a faked entity is
 * answered with a synthesized JSON body instead of the network. Nothing else in the app knows —
 * hooks, React Query keys, duplexer refetches and the components all behave exactly as they do
 * against the server.
 *
 * The whole model is ONE NUMBER PER ENTITY, persisted in localStorage:
 *
 *   - No number stored → that entity comes from the server, untouched.
 *   - A number stored  → the endpoint that owns it is answered locally with exactly that many.
 *
 * SUB-ENTITIES (a list that lives inside another entity) are the same one number, applied to every
 * parent — "3 tasks per workspace", "2 steps per task". They come in two flavours, and neither
 * needs its own screen:
 *
 *   - `within: '<parent>'` — carried INSIDE the parent's response (skills in /api/my-agents, plan
 *     steps in a task). The parent's builder reads the child's count via `ctx.count(childKey)`.
 *   - `scope: 'workspace'` — its own per-parent endpoint (`?workspaceId=`). One count serves every
 *     workspace, so whichever one you open shows that many.
 *
 * Faking a PARENT is enough to keep its children coherent: mocked children reference mocked
 * agent/workspace ids, and when a parent is NOT faked the mocked children hang off the real sample
 * workspace recorded at probe time, so names and links still resolve.
 *
 * Guardrails:
 *   - `devMockAvailable()` gates the whole module: a dev build, or a production tab the server has
 *     granted the dev tools to (Wix corporate network — lib/dev-tools.ts). A real user's browser
 *     holds no grant, so every entry point below is a no-op for them and counts a dev session left
 *     in localStorage are ignored.
 *   - GET only. Writes always go to the server — a mocked list is a read-time illusion. The ONE
 *     exception is a request naming a synthetic id, which is answered here whatever it asks for: such
 *     an entity exists in this browser alone, so the server can only refuse it (ids are GUIDs there)
 *     and the refusal lands in production logs. See `serveSynthetic`.
 *   - Adding an entity is one array entry: a label, its endpoint, how to count the real thing, and
 *     how to build N of the fake thing. Nothing else in the app changes.
 *
 * The ONE signal that doesn't own an `/api/` route is a workspace's Growth dashboard (`readByConsumer`):
 * a dashboard is server-rendered HTML behind a gateway route, so there is no JSON body to synthesize —
 * its reader (`lib/dashboard.ts`) asks `devMockDashboardEmpty()` instead. That still only fakes whether
 * the dashboard EXISTS; a count can't stand in for a page's contents, so the registry offers 0 or 1.
 */

import { agentBrainFolder, BRAIN_ROOT, SKILLS_ROOT } from './file-browser';
import { devToolsUnlocked } from './dev-tools';
import {
  GROUP_SEED_LAUNCH, GROUP_SEED_LAUNCH_PREVIEW, GROUP_SEED_TRANSCRIPT, GROUP_SEED_UNNAMED, GROUP_SEED_UNNAMED_PREVIEW, groupChatMockAnswer,
} from './group-chat-fixture';
import { parseGroupApproval, parseMaestroUnavailable } from './group-maestro';
import { parseGroupMessageMeta } from './group-chat';

/** Every fakeable entity. Spelled out so a typo in a registry entry or a builder fails to compile. */
export type MockEntityKey =
  | 'workspaces'
  | 'workspaceProjects'
  | 'agents'
  | 'skills'
  | 'opportunities'
  | 'piledUpJobs'
  | 'briefMessages'
  | 'conversations'
  | 'groupChats'
  | 'notifications'
  | 'notificationsUnread'
  | 'tasks'
  | 'taskPlanSteps'
  | 'routines'
  | 'knowledgeEntries'
  | 'recommendations'
  | 'contacts'
  | 'packs'
  | 'workspaceTasks'
  | 'workspaceFiles'
  | 'skillFolderFiles'
  | 'agentFolderFiles'
  | 'workspaceApps'
  | 'workspaceRoutines'
  | 'workspaceGoals'
  | 'goalTasks'
  | 'workspaceDashboard'
  | 'homeDashboard'
  | 'plan'
  | 'creditsCap'
  | 'creditsUsed'
  | 'dailyCreditsCap'
  | 'dailyCreditsUsed'
  | 'freeAgentRuns'
  | 'mobileAppInstalled'
  | 'signedInUser'
  | 'connectWhatsapp'
  | 'payments';

/**
 * `account` — one list for the whole account.
 * `workspace` — one list PER workspace, served off a `?workspaceId=` endpoint. The count applies to
 * every workspace, so any workspace you open shows that many.
 */
export type MockScope = 'account' | 'workspace';

export interface MockEntity {
  key: MockEntityKey;
  /** Row label in the dev panel. */
  label: string;
  /** One line under the label — where it shows up in the UI, or a caveat worth knowing. */
  hint?: string;
  scope: MockScope;
  /**
   * Set when this entity is carried inside ANOTHER entity's response, which therefore owns the
   * endpoint. The panel indents it under that parent; the parent's builder reads its count.
   */
  within?: MockEntityKey;
  /** The API path this entity owns. Absent when `within` or `readByConsumer` is set. */
  endpoint?: string;
  /**
   * Set when nothing under `/api/` carries this signal, so it cannot be answered by synthesizing a
   * response body — the code that reads it consults this module directly. Two entities: a
   * workspace's Growth dashboard and the home dashboard, both server-rendered HTML behind a gateway route.
   */
  readByConsumer?: true;
  /** How many the panel offers when the real count isn't known (a probe that failed). */
  fallback: number;
  /**
   * The most this entity can serve. Set it for a signal that is a YES/NO rather than a list —
   * `max: 1` turns the row into an off/on and keeps the round-trip honest, since a body that can only
   * say "installed" cannot count back to 7. Absent = unbounded, which is every list.
   */
  max?: number;
  /** How many of me a REAL response of the owning endpoint carries. Absent exactly when
   *  `readByConsumer` is set — no endpoint carries it, so there is nothing to count. */
  countIn?: (body: any) => number | undefined; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** The whole JSON body for the owning endpoint. Absent when `within` or `readByConsumer` is set. */
  body?: (ctx: MockContext) => unknown;
  /**
   * OTHER endpoints whose body this entity also changes, named by the entity that owns each.
   * Faking me must serve them too, or the app reads a mocked answer from one route and the
   * server's from another and shows two different stories about the same account — `plan`
   * decides the credits account's `isFreePlan`, so faking the plan alone would leave the
   * sidebar saying "Free Plan" while Settings named a paid tier.
   */
  influences?: MockEntityKey[];
  /**
   * OTHER endpoints this SAME row answers, because they report the same fact in a different shape —
   * each with its own body, since the shape differs. Distinct from `influences`, which names another
   * ROW whose own endpoint must also be served: a mirror has no row of its own, and must not, because
   * a second count for one fact is a second chance to disagree with itself.
   *
   * One case in tree: the payment ledger is asked twice — `/api/premium/invoices` for the rows and
   * `/api/premium/has-purchases` for the cheap "any at all?" the plan card's link reads. `endpoint`
   * stays the countable one (a boolean cannot count back to 4), so the probe and the stepper keep
   * working unchanged.
   */
  mirrors?: { endpoint: string; body: (ctx: MockContext) => unknown }[];
}

/** What a builder gets: the counts to honour, plus the request it is answering. */
export interface MockContext {
  /** The count to serve for any entity — the faked number, else the real one last seen, else the fallback. */
  count: (key: MockEntityKey) => number;
  /** Whether this entity is being faked (as opposed to counted from the server). */
  faked: (key: MockEntityKey) => boolean;
  /** A real workspace recorded when the panel last probed — what mocked children hang off. */
  sampleWorkspace?: { id: string; name: string; icon: string };
  /** The request's query string (`workspaceId`, `countOnly`, …). */
  params: URLSearchParams;
}

/**
 * Whether this browser may fake anything: a dev build, or a deployed origin where the server has
 * granted the dev tools to this tab (Wix corporate network — see lib/dev-tools.ts). Read on EVERY
 * call rather than resolved once, because on production the grant arrives during the page's life.
 */
export const devMockAvailable = (): boolean => devToolsUnlocked();

const STORAGE_KEY = 'orion-web:dev-mock-data';
const LOG = '[dev-mock]';

// ─── fixtures ────────────────────────────────────────────────────────────────────────────────────
// Small pools cycled by index, so a list of 30 reads like real content instead of "Item 30".

const WORKSPACE_NAMES = ['Marketing', 'Client work', 'Hiring', 'Product', 'Finance', 'Personal'];
const AGENT_NAMES = ['Ada', 'Boaz', 'Cleo', 'Dara', 'Eitan', 'Freya', 'Gil', 'Hana'];
const AGENT_TITLES = ['Scheduler', 'Researcher', 'Writer', 'Analyst', 'Recruiter', 'Bookkeeper'];
const SKILL_NAMES = [
  'Book meetings',
  'Draft follow-ups',
  'Watch the inbox',
  'Summarize calls',
  'Chase invoices',
  'Screen candidates',
  'Track competitors',
  'Plan travel',
];
const TASK_TITLES = [
  'Book the venue for the offsite',
  'Draft the weekly update',
  'Reschedule the Thursday sync',
  'Collect quotes from three caterers',
  'Follow up with the design candidate',
  'Reconcile last month’s invoices',
  'Summarize the customer calls',
  'Plan the launch checklist',
];
const PLAN_STEPS = [
  'Gather the details',
  'Draft the options',
  'Send for approval',
  'Book it',
  'Confirm with everyone',
  'File the notes',
];
const OPPORTUNITIES = [
  ['Turn the waitlist into bookings', 'I can reach out to the 42 people waiting and offer next week’s slots.'],
  ['Tighten your Monday mornings', 'Your first meeting keeps slipping. I can rebuild the block and defend it.'],
  ['Chase the three unpaid invoices', 'Two are over 30 days. I can send the reminders and log the replies.'],
  ['Warm up the quiet clients', 'Eleven clients haven’t heard from you in 60 days. I can draft a check-in each.'],
  ['Prep the quarterly review', 'I can pull the numbers, draft the deck and book the room.'],
];
const JOB_TITLES = [
  'Rescheduled 4 meetings around the flight',
  'Sent 12 follow-ups',
  'Filed the receipts from last week',
  'Answered 3 booking requests',
  'Drafted the newsletter',
  'Cleaned up the duplicate contacts',
];
const CONVERSATION_NAMES = [
  'Offsite planning',
  'Invoice chase',
  'New client onboarding',
  'Hiring: designer',
  'Travel to Berlin',
  'Weekly review',
  'Podcast outreach',
];
const NOTIFICATIONS = [
  ['Your Monday is booked', 'Three meetings moved to make room for the review.'],
  ['Invoice paid', 'Aviv Studio settled invoice 1043.'],
  ['Needs your call', 'Two caterers came back with different dates.'],
  ['Follow-ups sent', 'Twelve went out this morning.'],
  ['New booking request', 'Someone asked for Thursday at 14:00.'],
];
const ROUTINE_NAMES = ['Morning brief', 'Weekly recap', 'Inbox sweep', 'Invoice check', 'Lead follow-up', 'Calendar tidy'];
const ROUTINE_BEATS = ['Every day at 08:00', 'Mondays at 09:00', 'Every 4 hours', 'Fridays at 17:00', 'Every morning'];
const FILE_NAMES = [
  'offsite-plan.md',
  'catering-quotes.csv',
  'weekly-update.md',
  'invoice-1043.pdf',
  'candidate-notes.md',
  'launch-checklist.md',
];
const APP_NAMES = ['Booking page', 'Client portal', 'Quote calculator', 'Availability board'];
const GOAL_TITLES = [
  'Fill next month’s calendar',
  'Close the open invoices',
  'Hire a designer',
  'Launch the new package',
  'Win back quiet clients',
];
const KNOWLEDGE_TITLES = [
  'How I like my week',
  'The business, in one page',
  'Sam Taylor',
  'Meeting length preferences',
  'Calendar patterns',
  'Tone of voice',
  'Pricing rules',
  'Regular clients',
];
const RECOMMENDATIONS = [
  ['Confirm tomorrow’s meetings', 'I’ll message everyone and flag the no-replies.', 'calendar'],
  ['Draft the follow-ups', 'Ten calls from last week have no follow-up yet.', 'mail'],
  ['Summarize the week', 'A short recap of what moved and what didn’t.', 'summary'],
  ['Nudge the waitlist', 'Offer the slots that opened up.', 'message'],
];
const PACKS = [
  ['Client onboarding', 'Everything a new client needs on day one — a dashboard, an intake agent and the welcome routines.'],
  ['Weekly reporting', 'A reporting dashboard, a Monday recap routine and the skills to keep it current.'],
  ['Local SEO starter', 'Agents and knowledge to get a local business ranking, reviewed and found.'],
  ['Restaurant ops', 'A front-desk agent, a reservations dashboard and the daily-prep routines.'],
  ['Lead nurture', 'A follow-up agent, nurture routines and the templates to keep leads warm.'],
  ['Bookkeeping basics', 'Invoice chasing, expense capture and a month-end close checklist.'],
];

/** Statuses cycled across mocked tasks so every tab/stat card in the UI gets something. */
const TASK_STATUSES = ['ACTION_NEEDED', 'IN_PROGRESS', 'COMPLETED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];
const GOAL_STATUSES = ['ACTIVE', 'KICKOFF', 'ACHIEVED', 'ACTIVE'];
const KNOWLEDGE_GROUPS = ['self', 'business', 'contact', 'scheduling-habits', 'calendar-insights', 'other'];

const HOUR = 3600_000;

const seq = <T>(count: number, make: (i: number) => T): T[] =>
  Array.from({ length: Math.max(0, Math.floor(count)) }, (_, i) => make(i));

/** Cycle a pool by index, numbering the repeats so item 9 of 6 reads "Marketing 2", not "Marketing". */
function pick(pool: readonly string[], i: number): string {
  const lap = Math.floor(i / pool.length);
  return lap === 0 ? pool[i] : `${pool[i % pool.length]} ${lap + 1}`;
}

/** A pool of [a, b(, c)] rows, cycled the same way — the first cell carries the lap number. */
function pickRow(pool: readonly string[][], i: number): string[] {
  const row = pool[i % pool.length];
  const lap = Math.floor(i / pool.length);
  return lap === 0 ? row : [`${row[0]} ${lap + 1}`, ...row.slice(1)];
}

/**
 * Payment-history rows: [kind, credits granted, amount in MINOR units]. The kinds are the four the
 * modal's `describe` knows, so each renders its own label rather than falling through to the generic
 * one, and the amounts are plausible plan/top-up prices.
 */
const PAYMENTS: readonly [string, number, number][] = [
  ['SUBSCRIPTION_RENEWAL', 30000, 9900],
  ['TOPUP', 5000, 1900],
  ['SUBSCRIPTION_SWITCH', 30000, 4900],
  ['SUBSCRIPTION', 8000, 2900],
  ['TOPUP', 1000, 500],
];

const MOCK_ID_PREFIX = 'mock-';
const mockId = (kind: string, i: number): string => `${MOCK_ID_PREFIX}${kind}-${i + 1}`;
/** The routine the i-th mock task came from: two tasks in three, spread over the mocked routines. */
const mockTaskRoutine = (i: number, routines: number): { pulseIdentityId: string } | undefined =>
  i % 3 !== 1 && routines > 0 ? { pulseIdentityId: mockId('routine', Math.floor(i / 3) % routines) } : undefined;

/**
 * An id this module minted, naming something that exists in this browser and nowhere else.
 *
 * Matched on the SHAPE `mockId` produces — `mock-<kind>-<n>` — rather than the bare prefix, because the
 * values inspected are not all ids. A search box holding "mock-up" must still reach the server, and so
 * must a workspace someone really named "Mock 1", whose slug is `mock-1`.
 */
const MOCK_ID = new RegExp(`^${MOCK_ID_PREFIX}[a-z0-9]+(?:-[a-z0-9]+)*-\\d+$`);
const isMockId = (value: string | null | undefined): boolean => !!value && MOCK_ID.test(value);
const ago = (hours: number): string => new Date(Date.now() - hours * HOUR).toISOString();
const inDays = (days: number): string => new Date(Date.now() + days * 24 * HOUR).toISOString();
/** The next UTC midnight — the real boundary a daily allowance rolls on, so the mocked card
 *  exercises the same reset scheduling a live one does. */
const nextUtcMidnight = (): string => {
  const d = new Date();
  d.setUTCHours(24, 0, 0, 0);
  return d.toISOString();
};

/**
 * The plan ids a mocked subscription falls back to when the panel has never probed the catalog (a
 * count typed straight into localStorage, or a probe that failed). The tier still reads paid — which
 * is the point of the row — but nothing in the catalog resolves them, so the card falls back to its
 * generic "Your plan" name and offers no top-up. Real ids are recorded by `probeServerCounts`.
 */
const MOCK_PAID_PLAN_ID = mockId('plan-paid', 0);
const MOCK_MAX_PLAN_ID = mockId('plan-max', 0);

/** The agent a mocked child belongs to — cycled over however many agents are in play. */
const agentIndex = (ctx: MockContext, i: number): number => i % Math.max(1, ctx.count('agents'));

/**
 * The workspace a mocked child belongs to. When workspaces are faked it's one of the fakes; when
 * they aren't, it's the real workspace recorded at probe time, so the workspace name on an
 * opportunity card (and the link behind it) still resolves.
 */
function workspaceRef(ctx: MockContext, i: number): { id: string; name: string; icon: string } {
  if (!ctx.faked('workspaces') && ctx.sampleWorkspace) return ctx.sampleWorkspace;
  const k = i % Math.max(1, ctx.count('workspaces'));
  return { id: mockId('workspace', k), name: pick(WORKSPACE_NAMES, k), icon: '' };
}

/** The workspace a `?workspaceId=`-scoped list is being asked for — the real id when there is one. */
const scopedWorkspaceId = (ctx: MockContext): string => ctx.params.get('workspaceId') || mockId('workspace', 0);

function mockPlanSteps(ctx: MockContext): { title: string; completed: boolean }[] {
  const total = ctx.count('taskPlanSteps');
  return seq(total, (s) => ({ title: pick(PLAN_STEPS, s), completed: s < Math.floor(total / 2) }));
}

// ─── the registry ────────────────────────────────────────────────────────────────────────────────
// One entry per entity. `endpoint` + `body` own a route; `within` rides inside its parent's body.
// Dashboards are deliberately absent: a page is server-rendered HTML, not a list, so a count can't
// stand in for one.

export const MOCK_ENTITIES: MockEntity[] = [
  {
    key: 'workspaces',
    label: 'Workspaces',
    hint: 'Sidebar list + the catch-up page arrangement',
    scope: 'account',
    endpoint: '/api/workspaces',
    fallback: 3,
    countIn: (b) => b?.workspaces?.length,
    body: (ctx) => ({
      workspaces: seq(ctx.count('workspaces'), (i) => ({
        id: mockId('workspace', i),
        slug: `mock-workspace-${i + 1}`,
        name: pick(WORKSPACE_NAMES, i),
        icon: '',
        projects: ctx.count('workspaceProjects'),
      })),
    }),
  },
  {
    key: 'workspaceProjects',
    label: 'Projects in each workspace',
    hint: 'The child count under a workspace’s name',
    scope: 'account',
    within: 'workspaces',
    fallback: 2,
    countIn: (b) => b?.workspaces?.[0]?.projects,
  },
  {
    key: 'agents',
    label: 'Agents',
    hint: 'Your Agents roster, the genome, every agent dot',
    scope: 'account',
    endpoint: '/api/my-agents',
    fallback: 4,
    countIn: (b) => b?.agents?.length,
    body: (ctx) => ({
      agents: seq(ctx.count('agents'), (i) => ({
        id: mockId('agent', i),
        name: pick(AGENT_NAMES, i),
        title: pick(AGENT_TITLES, i),
        description: `Handles ${pick(SKILL_NAMES, i).toLowerCase()} and keeps you posted.`,
      })),
      // Every third skill comes from the genome, so both Skills sections on the Agents page fill.
      skills: seq(ctx.count('skills'), (i) => ({
        name: pick(SKILL_NAMES, i),
        hint: `So I can ${pick(SKILL_NAMES, i).toLowerCase()} without asking.`,
        source: i % 3 === 2 ? 'SKILL_SOURCE_GENOME' : 'SKILL_SOURCE_USER',
      })),
      newSkills: Math.min(2, ctx.count('skills')),
    }),
  },
  {
    key: 'skills',
    label: 'Skills',
    hint: 'Team-wide, on the Agents page',
    scope: 'account',
    within: 'agents',
    fallback: 6,
    countIn: (b) => b?.skills?.length,
  },
  {
    key: 'opportunities',
    label: 'Opportunities',
    hint: 'The catch-up pile of agent proposals',
    scope: 'account',
    endpoint: '/api/opportunities',
    fallback: 3,
    countIn: (b) => b?.opportunities?.length,
    body: (ctx) => ({
      opportunities: seq(ctx.count('opportunities'), (i) => {
        const [title, brief] = pickRow(OPPORTUNITIES, i);
        return { id: mockId('opportunity', i), title, brief, workspaceId: workspaceRef(ctx, i).id };
      }),
    }),
  },
  {
    key: 'piledUpJobs',
    label: 'Piled-up jobs',
    hint: '"Since you last checked in" on the catch-up page',
    scope: 'account',
    endpoint: '/api/piled-up-jobs',
    fallback: 5,
    countIn: (b) => b?.jobs?.length,
    body: (ctx) => ({
      jobs: seq(ctx.count('piledUpJobs'), (i) => {
        const workspace = workspaceRef(ctx, i);
        return {
          id: mockId('job', i),
          title: pick(JOB_TITLES, i),
          agentId: mockId('agent', agentIndex(ctx, i)),
          agentName: pick(AGENT_NAMES, agentIndex(ctx, i)),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          workspaceIcon: workspace.icon,
          // Mostly finished work, with one still in motion so the in-progress treatment shows.
          status: i % 4 === 0 ? 'inProgress' : 'completed',
          completedAt: Date.now() - (i + 1) * 0.75 * HOUR,
        };
      }),
    }),
  },
  {
    key: 'briefMessages',
    label: 'Brief turns',
    hint: 'The morning brief’s round-table. 0 = no brief at all',
    scope: 'account',
    endpoint: '/api/brief',
    fallback: 4,
    countIn: (b) => (b?.brief ? b.brief.messages?.length ?? 0 : 0),
    body: (ctx) => {
      const total = ctx.count('briefMessages');
      if (total <= 0) return { brief: null };
      return {
        brief: {
          conversationId: mockId('brief-conversation', 0),
          generatedDate: ago(2),
          teaser: 'Three things moved overnight, and one needs you.',
          // The LAST turn is the round-table wrap-up (`agentMeetingSummary`) the glance headlines
          // with; the rest are agent turns, which is what the avatar row is built from.
          messages: seq(total, (i) => ({
            id: mockId('brief-message', i),
            content:
              i === total - 1
                ? 'Overnight: 4 meetings moved, 12 follow-ups out, one catering decision waiting on you.'
                : `${pick(AGENT_NAMES, agentIndex(ctx, i))} here — ${pick(JOB_TITLES, i).toLowerCase()}.`,
            createdDate: ago(2 + (total - i) * 0.05),
            metadata:
              i === total - 1
                ? { agentMeetingSummary: 'true' }
                : { agentId: mockId('agent', agentIndex(ctx, i)) },
          })),
        },
      };
    },
  },
  {
    key: 'conversations',
    label: 'Chats',
    hint: 'The sidebar’s conversation list (a few are WhatsApp)',
    scope: 'account',
    endpoint: '/api/conversations',
    fallback: 8,
    countIn: (b) => b?.conversations?.length,
    body: (ctx) => {
      // The Chats page asks the `chats-v1` contract, which needs a read state on every row and an `asOf`.
      const chatsV1 = ctx.params.get('contract') === 'chats-v1';
      const readState = { lastReadAt: null, lastReadCursor: null, hasUnreadMessages: false, hasUnreadNotifications: false, versions: { reader: null, messageHead: null, notifications: null } };
      const rows = [...groupChatRows(ctx.count('groupChats')), ...conversationRows(ctx)];
      return chatsV1
        ? { conversations: rows.map((row) => ({ ...row, readState })), nextCursor: null, asOf: new Date().toISOString() }
        : { conversations: rows, hasMore: false };
    },
  },
  {
    key: 'groupChats',
    label: 'Group chats',
    hint: 'Symphony group chats at the top of every chat list (0–2). Turn on symphonyGroupChat in Feature toggles to see the group look; "Launch crew" has a transcript',
    scope: 'account',
    within: 'conversations',
    fallback: 0,
    max: 2,
    countIn: (b) => (b?.conversations ?? []).filter((c: { group?: unknown }) => !!c?.group).length,
  },
  {
    key: 'notifications',
    label: 'Notifications',
    hint: 'The bell feed',
    scope: 'account',
    endpoint: '/api/notifications',
    fallback: 6,
    countIn: (b) => b?.notifications?.length,
    body: (ctx) => {
      const total = ctx.count('notifications');
      const unread = Math.min(ctx.count('notificationsUnread'), total);
      // The unread badge asks the same endpoint for a count only.
      if (ctx.params.get('countOnly')) return { unreadCount: unread };
      return {
        notifications: seq(total, (i) => {
          const [title, body] = pickRow(NOTIFICATIONS, i);
          return { id: mockId('notification', i), title, body, date: ago(i * 5 + 1), read: i >= unread, conversationId: null };
        }),
        nextCursor: null,
      };
    },
  },
  {
    key: 'notificationsUnread',
    label: 'Unread among them',
    hint: 'Drives the bell badge',
    scope: 'account',
    within: 'notifications',
    fallback: 2,
    countIn: (b) => b?.notifications?.filter((n: { read?: boolean }) => !n.read).length,
  },
  {
    key: 'tasks',
    label: 'Agent tasks',
    hint: 'The account-wide Tasks screen (statuses are spread across every tab)',
    scope: 'account',
    endpoint: '/api/tasks',
    fallback: 7,
    countIn: (b) => b?.tasks?.length,
    body: (ctx) => ({
      tasks: seq(ctx.count('tasks'), (i) => {
        const action = `Working on "${pick(PLAN_STEPS, i).toLowerCase()}"`;
        return {
          id: mockId('task', i),
          digestId: mockId('task', i),
          createdDate: ago(i * 6 + 4),
          updatedDate: ago(i * 6),
          conversationId: mockId('conversation', i),
          title: pick(TASK_TITLES, i),
          status: TASK_STATUSES[i % TASK_STATUSES.length],
          supervisorCurrentAction: action,
          currentAction: action,
          summary: `${pick(AGENT_NAMES, agentIndex(ctx, i))} is on it.`,
          plan: mockPlanSteps(ctx),
          // Two tasks in three came from a mocked routine, so the detail's "From routine" line shows in
          // every tab (the statuses cycle through all four) and the list's routine fold has something
          // to show. Spread over the routines.
          ...(mockTaskRoutine(i, ctx.count('routines')) ?? {}),
        };
      }),
    }),
  },
  {
    key: 'taskPlanSteps',
    label: 'Plan steps in each task',
    hint: 'The Master Plan checklist on a task',
    scope: 'account',
    within: 'tasks',
    fallback: 4,
    countIn: (b) => b?.tasks?.[0]?.plan?.length,
  },
  {
    key: 'routines',
    label: 'Routines',
    hint: 'The account-wide Routines screen',
    scope: 'account',
    endpoint: '/api/pulses',
    fallback: 4,
    countIn: (b) => b?.pulses?.length,
    body: (ctx) => ({
      pulses: seq(ctx.count('routines'), (i) => ({
        id: mockId('routine', i),
        revision: '1',
        createdDate: ago(i * 24 + 72),
        updatedDate: ago(i * 24),
        name: pick(ROUTINE_NAMES, i),
        description: `Keeps ${pick(SKILL_NAMES, i).toLowerCase()} on rails.`,
        heartbeat: pick(ROUTINE_BEATS, i),
        cron: '0 8 * * *',
        default: false,
        // One in five paused, so the off state shows without hiding everything.
        paused: i % 5 === 4,
        workspaceId: workspaceRef(ctx, i).id,
        display: {},
      })),
    }),
  },
  {
    key: 'knowledgeEntries',
    label: 'Knowledge entries',
    hint: 'Spread across the Knowledge screen’s groups',
    scope: 'account',
    endpoint: '/api/knowledge',
    fallback: 8,
    countIn: (b) => b?.groups?.reduce((sum: number, g: { entries?: unknown[] }) => sum + (g.entries?.length ?? 0), 0),
    body: (ctx) => {
      const entries = seq(ctx.count('knowledgeEntries'), (i) => ({
        group: KNOWLEDGE_GROUPS[i % KNOWLEDGE_GROUPS.length],
        entry: {
          path: `${KNOWLEDGE_GROUPS[i % KNOWLEDGE_GROUPS.length]}/mock-${i + 1}.md`,
          title: pick(KNOWLEDGE_TITLES, i),
          subtitle: 'Learned from your calendar and chats.',
          content: `# ${pick(KNOWLEDGE_TITLES, i)}\n\nMock knowledge entry ${i + 1}.`,
        },
      }));
      return {
        groups: KNOWLEDGE_GROUPS.map((id) => ({ id, entries: entries.filter((e) => e.group === id).map((e) => e.entry) })).filter(
          (g) => g.entries.length > 0,
        ),
      };
    },
  },
  {
    key: 'recommendations',
    label: 'Recommendations',
    hint: 'The chat’s suggestion grid — split between task and routine ideas',
    scope: 'account',
    endpoint: '/api/recommendations',
    fallback: 4,
    countIn: (b) => (b?.taskRecommendations?.length ?? 0) + (b?.pulseRecommendations?.length ?? 0),
    body: (ctx) => {
      const total = ctx.count('recommendations');
      const make = (i: number) => {
        const [name, description, iconSlug] = pickRow(RECOMMENDATIONS, i);
        return { name, description, iconSlug };
      };
      return {
        taskRecommendations: seq(Math.ceil(total / 2), (i) => make(i)),
        pulseRecommendations: seq(Math.floor(total / 2), (i) => make(i + Math.ceil(total / 2))),
      };
    },
  },
  {
    key: 'contacts',
    label: 'Contacts',
    hint: 'The composer’s contact picker',
    scope: 'account',
    endpoint: '/api/contacts',
    fallback: 10,
    countIn: (b) => (Array.isArray(b) ? b.length : undefined),
    body: (ctx) =>
      seq(ctx.count('contacts'), (i) => ({
        id: mockId('contact', i),
        name: `${pick(AGENT_NAMES, i)} ${pick(['Taylor', 'Cohen', 'Ruiz', 'Okafor', 'Novak'], i)}`,
        phones: [`+1555010${String(100 + i).slice(-3)}`],
        emails: [`${pick(AGENT_NAMES, i).toLowerCase().replace(/\s/g, '')}@example.com`],
      })),
  },
  {
    key: 'packs',
    label: 'Partner packs',
    hint: 'The Symphony Packs list — 0 shows the empty state; every fourth is a quick share',
    scope: 'account',
    endpoint: '/api/packs',
    fallback: 4,
    // /api/packs answers a `{ result: { packs, profile, defaults } }` envelope, so count inside it.
    countIn: (b) => b?.result?.packs?.length,
    body: (ctx) => ({
      result: {
        packs: seq(ctx.count('packs'), (i) => {
          const [name, description] = pickRow(PACKS, i);
          const quickShare = i % 4 === 3;
          const shared = i % 3 !== 0;
          return {
            slug: `mock-pack-${i + 1}`,
            name,
            description,
            shared,
            disabled: i % 5 === 4,
            quickShare,
            itemCounts: {
              dashboards: (i % 3) + 1,
              agents: (i % 2) + 1,
              pulses: i % 3,
              skills: i % 4,
              knowledge: i % 2,
              playbooks: i % 2,
            },
            acceptedCount: i % 4,
            sharedCount: shared ? (i % 3) + 1 : 0,
          };
        }),
        profile: { name: 'Your studio', icon: '🧩', logoUrl: '', tagline: 'Built with Symphony', description: '', url: '' },
        defaults: { name: 'Your studio', tagline: 'Built with Symphony', description: '', url: '' },
      },
    }),
  },
  {
    key: 'workspaceDashboard',
    label: 'Growth dashboard in a workspace',
    hint: '0 = the Growth Insights tab shows its empty state (as does any dashboard opened by slug). 1 = the real one',
    scope: 'workspace',
    // No endpoint: a dashboard is server-rendered HTML behind a gateway route, so there is no body to
    // synthesize and nothing to count — `lib/dashboard.ts` reads the stored 0/1 itself. A yes/no, not a
    // list, so `max: 1` — see `max` on MockEntity.
    readByConsumer: true,
    fallback: 1,
    max: 1,
  },
  {
    key: 'homeDashboard',
    label: 'Home dashboard',
    hint: '0 = the home shows its empty state — the "What\'s your goal?" picker on the bands arm, the "One home for everything" intro otherwise. 1 = the real one',
    scope: 'account',
    // The home's twin of `workspaceDashboard`: the same gateway-rendered HTML, the same yes/no, read by
    // `lib/dashboard.ts` for the target with no slug. The home's empty state is the hardest one to reach on
    // a real account (any account that ever built a home keeps it), and it is where a new user lands.
    readByConsumer: true,
    fallback: 1,
    max: 1,
  },
  {
    key: 'workspaceTasks',
    label: 'Tasks in a workspace',
    hint: '"Agents at work" — feeds the waiting / in-progress / done cards',
    scope: 'workspace',
    endpoint: '/api/workspace-tasks',
    fallback: 6,
    countIn: (b) => b?.tasks?.length,
    body: (ctx) => ({
      tasks: seq(ctx.count('workspaceTasks'), (i) => ({
        id: mockId('agent-task', i),
        revision: '1',
        createdDate: ago(i * 6 + 4),
        updatedDate: ago(i * 6),
        title: pick(TASK_TITLES, i),
        brief: `Mock agent task ${i + 1}.`,
        status: TASK_STATUSES[i % TASK_STATUSES.length],
        agentId: mockId('agent', agentIndex(ctx, i)),
        workspaceId: scopedWorkspaceId(ctx),
        sourceConversationId: mockId('conversation', i),
        plan: mockPlanSteps(ctx),
        display: { currentAction: `Working on "${pick(PLAN_STEPS, i).toLowerCase()}"`, summary: 'Mock progress.' },
      })),
    }),
  },
  {
    key: 'workspaceFiles',
    label: 'Files & apps in a workspace',
    hint: 'The files strip and gallery. Every fourth one is an app',
    scope: 'workspace',
    endpoint: '/api/workspace-files',
    fallback: 6,
    countIn: (b) => b?.items?.length,
    body: (ctx) => {
      const workspaceItems = seq(ctx.count('workspaceFiles'), (i) => {
        const isApp = i % 4 === 3;
        const name = isApp ? pick(APP_NAMES, i) : pick(FILE_NAMES, i);
        return {
          name,
          isApp,
          // The id a share is granted against ("Share to group…" and "Shared with groups" need it).
          ...(isApp ? {} : { fileId: mockId('file', i) }),
          extension: isApp ? undefined : name.split('.').pop(),
          content: isApp ? undefined : `# ${name}\n\nMock artifact ${i + 1}, written by an agent.`,
          builtBy: { agentId: mockId('agent', agentIndex(ctx, i)), name: pick(AGENT_NAMES, agentIndex(ctx, i)) },
          // The route answers ACCOUNT-WIDE when no workspaceId is asked for, and every item then names
          // its own Space (the cross-workspace `/files` browser and the Files tile both join on it), so
          // the unscoped mock has to spread across the mocked Spaces rather than omit the field.
          workspaceId: ctx.params.get('workspaceId') || workspaceRef(ctx, i).id,
          // Recent enough that the first two exercise the tiles' "N new" badge.
          createdDate: ago(i * 12),
        };
      });
      // Skills and an agent's own notes carry NO workspaceId — they sit at the top of the MERGED tree
      // (`SKILLS_ROOT` / `BRAIN_ROOT`), not under any one Space, exactly like the real route's
      // account-wide read. `FilesBrowser` narrows by PATH (`filePath`), and neither root starts with a
      // workspace id, so a Space's own gallery never shows them regardless of whether this body was
      // asked with a `workspaceId` or not — there is no need to gate on that here.
      const skillItems = seq(ctx.count('skillFolderFiles'), (i) => {
        const title = pick(SKILL_NAMES, i);
        return {
          name: `${SKILLS_ROOT}/${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.md`,
          extension: 'md',
          content: `# ${title}\n\nMock skill file ${i + 1}.`,
          createdDate: ago(i * 20 + 48),
        };
      });
      const agentItems = seq(ctx.count('agentFolderFiles'), (i) => {
        const agent = pick(AGENT_NAMES, agentIndex(ctx, i));
        return {
          name: `${agentBrainFolder(agent)}/mock-note-${i + 1}.md`,
          extension: 'md',
          content: `# Note for ${agent}\n\nMock agent note ${i + 1}.`,
          builtBy: { agentId: mockId('agent', agentIndex(ctx, i)), name: agent },
          createdDate: ago(i * 15 + 24),
        };
      });
      return { items: [...workspaceItems, ...skillItems, ...agentItems] };
    },
  },
  {
    key: 'skillFolderFiles',
    label: 'Files under the Skills folder',
    hint: 'The account-wide Files browser’s "Skills" folder — files that belong to no Space',
    scope: 'workspace',
    within: 'workspaceFiles',
    // Not merged into the parent's own count above (see the note there) — 0 unless explicitly faked,
    // which also matches a scoped probe honestly: a real per-workspace read never carries the
    // account's Skills folder either.
    fallback: 0,
    countIn: (b) => b?.items?.filter((item: { name?: string }) => item?.name?.startsWith(`${SKILLS_ROOT}/`)).length,
  },
  {
    key: 'agentFolderFiles',
    label: 'Files under the Agents folder',
    hint: 'The account-wide Files browser’s "Agents" folder — one mock note per agent',
    scope: 'workspace',
    within: 'workspaceFiles',
    fallback: 0,
    countIn: (b) => b?.items?.filter((item: { name?: string }) => item?.name?.startsWith(`${BRAIN_ROOT}/`)).length,
  },
  {
    key: 'workspaceApps',
    label: 'Apps in a workspace',
    hint: 'The Apps screen — every app your agents built in that Space',
    scope: 'workspace',
    endpoint: '/api/workspace-apps',
    fallback: 4,
    countIn: (b) => b?.apps?.length,
    body: (ctx) => ({
      apps: seq(ctx.count('workspaceApps'), (i) => ({
        id: mockId('app', i),
        name: pick(APP_NAMES, i),
        // A believable address rather than a real one — the card only opens it, never fetches it.
        url: `https://mock-app-${i + 1}.example.com`,
        description: `Built by ${pick(AGENT_NAMES, agentIndex(ctx, i))} to ${pick(SKILL_NAMES, i).toLowerCase()}.`,
        // Every third one never opened, so the "Never opened" badge shows alongside the "X ago" ones.
        lastOpenedAt: i % 3 === 0 ? undefined : ago(i * 3),
      })),
    }),
  },
  {
    key: 'workspaceRoutines',
    label: 'Routines in a workspace',
    hint: 'The workspace’s routines grid',
    scope: 'workspace',
    endpoint: '/api/workspace-routines',
    fallback: 3,
    countIn: (b) => b?.pulses?.length,
    body: (ctx) => ({
      pulses: seq(ctx.count('workspaceRoutines'), (i) => ({
        id: mockId('workspace-routine', i),
        revision: '1',
        createdDate: ago(i * 24 + 72),
        updatedDate: ago(i * 24),
        name: pick(ROUTINE_NAMES, i),
        description: `Keeps ${pick(SKILL_NAMES, i).toLowerCase()} on rails.`,
        heartbeat: pick(ROUTINE_BEATS, i),
        cron: '0 8 * * *',
        default: false,
        paused: i % 5 === 4,
        workspaceId: scopedWorkspaceId(ctx),
        display: {},
      })),
    }),
  },
  {
    key: 'workspaceGoals',
    label: 'Goals in a workspace',
    hint: 'The workspace’s Goals tab',
    scope: 'workspace',
    endpoint: '/api/goals',
    fallback: 3,
    countIn: (b) => b?.goals?.length,
    body: (ctx) => ({
      goals: seq(ctx.count('workspaceGoals'), (i) => {
        const tasks = seq(ctx.count('goalTasks'), (t) => ({
          id: mockId(`goal-${i + 1}-task`, t),
          title: pick(TASK_TITLES, t),
          status: TASK_STATUSES[t % TASK_STATUSES.length],
          agentId: mockId('agent', agentIndex(ctx, t)),
          steps: mockPlanSteps(ctx),
        }));
        const done = tasks.filter((t) => t.status === 'COMPLETED').length;
        return {
          id: mockId('goal', i),
          title: pick(GOAL_TITLES, i),
          brief: 'Mock goal — what the agents are driving at.',
          status: GOAL_STATUSES[i % GOAL_STATUSES.length],
          slug: `mock-goal-${i + 1}`,
          workspaceId: scopedWorkspaceId(ctx),
          conversationId: mockId('conversation', i),
          percent: tasks.length ? Math.round((done / tasks.length) * 100) : 0,
          tasks,
        };
      }),
    }),
  },
  {
    key: 'goalTasks',
    label: 'Tasks in each goal',
    hint: 'The plan rows under a goal',
    scope: 'workspace',
    within: 'workspaceGoals',
    fallback: 3,
    countIn: (b) => b?.goals?.[0]?.tasks?.length,
  },
  {
    key: 'signedInUser',
    label: 'Signed-in user',
    hint: '0 = signed out · 1 = a mock account, so the rail avatar and credits card render with NO Wix session',
    scope: 'account',
    endpoint: '/api/me',
    fallback: 1,
    // A yes/no, not a list — see `max` on MockEntity.
    max: 1,
    countIn: (b) => (b?.userId ? 1 : 0),
    /**
     * The one entity that unblocks every OTHER row when there is no session at all.
     *
     * `/api/me` gates most of the app: the rail's account card returns early without initials, so the
     * credits card inside it never mounts and its own mocked rows can't be seen. A dev-proxy session
     * expires roughly daily (`WIX_SESSION_DEV`), and until this row existed that took the whole panel
     * with it — every count still applied, but nothing that reads them was on screen. Faking the
     * signed-in user is what lets the panel be used offline.
     *
     * At 0 it serves an EMPTY object rather than a 404: `ensureMe` reads a non-ok response as `null`,
     * and an empty body reaches the same place through the success path, so "signed out" is a real
     * answer here rather than a simulated failure.
     */
    body: (ctx) =>
      ctx.count('signedInUser') > 0
        ? {
            userId: mockId('user', 0),
            firstName: 'Dev',
            lastName: 'User',
            recipientName: 'Dev User',
            email: 'dev@example.com',
            userName: 'dev@example.com',
          }
        : {},
  },
  {
    key: 'plan',
    label: 'Plan tier',
    hint: '0 = Free · 1 = Paid · 2 = Max+ (top tier). Moves the card title, the CTA and the top-up button',
    scope: 'account',
    endpoint: '/api/premium',
    fallback: 0,
    // Three states, not a list — the stepper walks Free → Paid → Max+ and stops.
    max: 2,
    // The tier also decides the credits account's `isFreePlan`, which is what the sidebar card
    // reads; without this, faking the plan alone would leave the two surfaces disagreeing.
    influences: ['creditsCap'],
    countIn: (b) => {
      const sub = (b?.premium?.subscriptions ?? []).find(
        (s: { status?: string; providerSubscriptionId?: string }) =>
          !!s.providerSubscriptionId && (s.status === 'ACTIVE' || s.status === 'PAST_DUE'),
      );
      if (!sub) return 0;
      // Max+ is decided against the catalog, exactly as `activePlanIsMaxPlus` does; if the probe
      // could not read the catalog we can still honestly say "paid".
      const maxId = readState().samplePlanIds?.maxPlus;
      return sub.planId === MOCK_MAX_PLAN_ID || (!!maxId && sub.planId === maxId) ? 2 : 1;
    },
    body: (ctx) => {
      const tier = ctx.count('plan');
      const ids = readState().samplePlanIds;
      const planId = tier >= 2 ? (ids?.maxPlus ?? MOCK_MAX_PLAN_ID) : tier === 1 ? (ids?.paid ?? MOCK_PAID_PLAN_ID) : null;
      const balance = Math.max(0, ctx.count('creditsCap') - ctx.count('creditsUsed'));
      return {
        premium: {
          creditsBalance: balance,
          subscriptionCredits: balance,
          topupCredits: 0,
          // A subscription carries a `providerSubscriptionId` iff it is genuinely paid — the rule
          // `isPaidActiveSubscription` enforces — so the free tier is an EMPTY list rather than an
          // unpaid record, and the paid tiers get one (which is also what turns the cancel footer on).
          subscriptions: planId
            ? [
                {
                  planId,
                  status: 'ACTIVE',
                  period: 'MONTHLY',
                  providerSubscriptionId: mockId('subscription', 0),
                  currentPeriodEnd: inDays(30),
                },
              ]
            : [],
        },
      };
    },
  },
  {
    key: 'creditsCap',
    label: 'Monthly credits',
    hint: 'The allowance the meter measures against — the "of {cap}" half',
    scope: 'account',
    endpoint: '/api/credits/balance',
    fallback: 500,
    countIn: (b) => b?.account?.combinedAllotment ?? undefined,
    body: (ctx) => {
      const cap = ctx.count('creditsCap');
      // Clamped so a used count above the cap can't serve a negative balance, which every meter
      // would then render as more-than-full.
      const balance = Math.max(0, cap - ctx.count('creditsUsed'));
      // Today's ceiling rides the same body, because it arrives on the same response. A limit of 0
      // is the real "no ceiling applies" signal — the BFF omits the object and the client reads a
      // non-positive limit as absent — so the row doubles as the on/off for the whole Daily bar.
      const dailyCap = ctx.count('dailyCreditsCap');
      const dailyUsed = ctx.count('dailyCreditsUsed');
      return {
        hasMetaSite: true,
        account: {
          resetDateIso: inDays(30),
          // One number drives both routes — see `influences` on `plan`.
          isFreePlan: ctx.count('plan') === 0,
          combinedCredits: balance,
          // `null`, never 0 — the shape the REAL route serves. `combinedCreditsFromOverview`
          // collapses a non-positive allotment (which symphony-bff returns whenever its catalog
          // enrichment fails, alongside a still-authoritative `dailyLimit`) so the meter reads
          // "unknown" rather than "no allowance". Emitting a raw 0 here made this panel unable to
          // reproduce the one state where the two pools come apart — the state a peer review found a
          // bug in — because a 0 cap resolves to a meter and a null cap resolves to none.
          combinedAllotment: cap > 0 ? cap : null,
          dailyLimit: {
            limit: dailyCap,
            used: dailyUsed,
            // NOT clamped to the cap: the turn that crosses the ceiling is charged in full, so an
            // over-spend is a real state the bar has to render honestly.
            remaining: Math.max(0, dailyCap - dailyUsed),
            resetsAtIso: nextUtcMidnight(),
          },
        },
        real: ctx.count('freeAgentRuns'),
      };
    },
  },
  {
    key: 'creditsUsed',
    label: '…of them used',
    hint: 'Drives the bar’s width AND its colour: green under 50%, orange to 85%, red above',
    scope: 'account',
    within: 'creditsCap',
    fallback: 120,
    countIn: (b) => {
      const cap = b?.account?.combinedAllotment;
      const balance = b?.account?.combinedCredits;
      if (typeof cap !== 'number' || typeof balance !== 'number') return undefined;
      return Math.max(0, Math.min(cap, cap - balance));
    },
  },
  {
    key: 'dailyCreditsCap',
    label: 'Daily credit limit',
    hint: 'Today’s ceiling — a separate pool. 0 = no daily limit, which hides the Daily bar entirely',
    scope: 'account',
    within: 'creditsCap',
    fallback: 100,
    countIn: (b) => b?.account?.dailyLimit?.limit ?? undefined,
  },
  {
    key: 'dailyCreditsUsed',
    label: '…of them used today',
    hint: 'Set it above the limit to see the over-spend the crossing turn actually produces',
    scope: 'account',
    within: 'creditsCap',
    fallback: 90,
    countIn: (b) => b?.account?.dailyLimit?.used ?? undefined,
  },
  {
    key: 'freeAgentRuns',
    label: 'Free agent runs left',
    hint: 'A separate pool from credits — its own line on the plan card. 0 hides the line',
    scope: 'account',
    within: 'creditsCap',
    fallback: 3,
    countIn: (b) => (typeof b?.real === 'number' ? b.real : undefined),
  },
  {
    key: 'mobileAppInstalled',
    label: 'Mobile app installed',
    hint: '0 = never had it, so the home offers the download-the-app QR card. 1 = has it, card hidden',
    scope: 'account',
    endpoint: '/api/mobile-app-status',
    fallback: 0,
    // A yes/no, not a list — see `max` on MockEntity.
    max: 1,
    // Only a confirmed `true` counts as installed: the real route answers `null` when it could not
    // find out, and the card treats that as "say nothing" exactly like a `false`.
    countIn: (b) => (b?.installed === true ? 1 : 0),
    body: (ctx) => ({ installed: ctx.count('mobileAppInstalled') > 0 }),
  },
  {
    key: 'connectWhatsapp',
    label: 'Connect WhatsApp nav item',
    hint: '0 = hidden (user has a phone) · 1 = shown (user has no phone)',
    scope: 'account',
    endpoint: '/api/recipient',
    fallback: 0,
    // A yes/no — 0 hides the item (phone present), 1 shows it (phone absent).
    max: 1,
    countIn: (b) => (b?.recipient?.phone ? 0 : 1),
    body: (ctx) => ({
      recipient: {
        id: mockId('recipient', 0),
        // The chats page keys its reads on email + metaSiteId, so a mocked recipient needs both to open a chat.
        metaSiteId: mockId('metasite', 0),
        email: 'dev@example.com',
        name: 'Dev User',
        finishedOnboarding: true,
        onboardingState: 'COMPLETED',
        // When the toggle is on (1), omit the phone so the rail item appears.
        ...(ctx.count('connectWhatsapp') > 0 ? {} : { phone: '+15550100001' }),
      },
    }),
  },
  {
    key: 'payments',
    label: 'Payment history transactions',
    hint: '0 hides the plan card’s “Payment history” link entirely; any number shows it and fills the modal',
    scope: 'account',
    endpoint: '/api/premium/invoices',
    fallback: 4,
    // The real route pages at 12, and the probe reads only the FIRST page — so a longer ledger reads
    // as 12 here. The row answers "any, and roughly how many", not "audit my account".
    countIn: (b) => b?.invoices?.length,
    // The SAME ledger, asked a second and cheaper way: `has-purchases` answers a boolean off the very
    // same purchases, and the plan card's link reads THAT rather than the paged list. Two routes, one
    // fact, so one row — see `mirrors`. Without it the link would keep reading the server's answer
    // while the modal behind it showed a mocked list, which is the exact disagreement the panel exists
    // to avoid.
    mirrors: [{ endpoint: '/api/premium/has-purchases', body: (ctx) => ({ hasPurchases: ctx.count('payments') > 0 }) }],
    body: (ctx) => ({
      // One page, and `null` ends the paging — a fake has nothing to paginate, and the modal keeps
      // asking for the next cursor until it gets this.
      nextCursor: null,
      invoices: seq(ctx.count('payments'), (i) => {
        const [kind, grantedCredits, amountMinor] = PAYMENTS[i % PAYMENTS.length];
        return {
          purchaseId: mockId('purchase', i),
          // Newest first, as the route serves them — one a month back from today.
          createdAt: Date.now() - i * 30 * 24 * HOUR,
          kind,
          // One refunded row (from the second) so the status chip renders at all; the rest completed.
          status: i === 1 ? 'REFUNDED' : 'COMPLETED',
          grantedCredits,
          amountMinor,
          currency: 'USD',
          // No links: a fake Stripe URL is a dead link, and the row renders correctly without one
          // (`hostedInvoiceUrl || receiptUrl || null` → no anchor), which is a real server case too.
          hostedInvoiceUrl: null,
          receiptUrl: null,
        };
      }),
    }),
  },
];

const BY_KEY = new Map<MockEntityKey, MockEntity>(MOCK_ENTITIES.map((e) => [e.key, e]));

/**
 * The response for ONE synthetic entity, addressed by an id in the path.
 *
 * Deliberately not panel rows: a detail is not a list, so there is no count to offer — the same reason
 * dashboards are absent from the registry above. These exist so a mocked card can be CLICKED, and each
 * body is the shape its real route returns, kept minimal on purpose. A richer fake is a fake that
 * drifts, and a wrong shape misleads worse than a missing one.
 */
/** The conversation id whose transcript is the question-after-cards reproduction — the newest mocked chat. */
export const QUESTION_AFTER_CARDS_CONVERSATION = 'mock-conversation-qac-1';

/**
 * The transcript `questionAfterCards` exists for: an ASSISTANT reply carrying `choices` followed by a run
 * of own-row cards, which is what a tool that finished after the reply — or a background task landing
 * minutes later — leaves behind. Rendered in arrival order the ask is off the top of the viewport and only
 * the cards are on screen.
 *
 * `?cards=` picks the trailing cards, "+"-joined, in the order they land. Kept in step with the phones'
 * SYMPHONY_CHAT_SEED_QUESTION_AFTER_CARDS so the three surfaces can be compared frame for frame.
 */
function questionAfterCardsThread(ctx: MockContext): unknown[] {
  // `/api/conversations/:id/messages` returns rows already mapped to `Message` (see `toLightMessage`),
  // so the markers are top-level fields here rather than `metadata`.
  const wanted = (ctx.params.get('cards') || 'success+genome').split('+').filter(Boolean);
  const row = (id: string, role: string, content: string, extra: Record<string, unknown> = {}) => ({
    id,
    conversationId: QUESTION_AFTER_CARDS_CONVERSATION,
    role,
    content,
    createdDate: ago(1),
    ...extra,
  });
  const cards = wanted.map((kind, i) => {
    const id = `qac-card-${i}`;
    if (kind === 'genome') {
      return row(id, 'ASSISTANT', '\u2726 Alex joined your team', {
        agentCard: { id: 'seed-agent-alex', name: 'Alex', title: 'Web Presence Agent' },
      });
    }
    if (kind === 'atwork') {
      return row(id, 'ASSISTANT', '\u2726 Alex is working on your Clothing Factory page', {
        agentAtWorkCard: { taskId: 'mock-agent-task-1', agentName: 'Alex', taskTitle: 'Build the Clothing Factory page' },
      });
    }
    if (kind === 'gift') {
      return row(id, 'ASSISTANT', '\u2726 200 credits added to your balance', {
        creditGiftCard: { kind: 'appreciation', credits: 200 },
      });
    }
    return row(id, 'ASSISTANT', '\u2713 Clothing Factory workspace created', {
      milestoneCard: { kind: 'workspace', name: 'Clothing Factory', subtitle: "Your factory's command center" },
    });
  });
  return [
    row('qac-user', 'USER', 'I run a clothing factory, help me get online'),
    row('qac-step', 'ASSISTANT', 'Let me hire the right agent to build this for you.'),
    row(
      'qac-ask',
      'ASSISTANT',
      'Meet **Alex**, your new Web Presence Agent! Alex will build your Clothing Factory page, complete with your contact details and a form so customers can reach you.\n\nOne quick thing before Alex gets started \u2014 most of what your team does happens in the background, and a phone number is how you hear when something needs you. Would you like to add yours so you don\u2019t miss anything?',
      {
        choices: {
          askId: 'phone',
          options: [
            { id: 'yes', label: 'Yes, add my number' },
            { id: 'skip', label: 'Skip for now' },
          ],
        },
      },
    ),
    ...cards,
  ];
}

/** The plain mocked chats — spread over days so the sidebar's Today / Yesterday / Earlier sections all fill. */
function conversationRows(ctx: MockContext): Record<string, unknown>[] {
  return seq(ctx.count('conversations'), (i) => ({
    // The newest row is the question-after-cards reproduction, so the transcript that `questionAfterCards`
    // exists for is one click away. Every other row is an empty thread as before.
    id: i === 0 ? QUESTION_AFTER_CARDS_CONVERSATION : mockId('conversation', i),
    name: i === 0 ? 'Question after cards (demo)' : pick(CONVERSATION_NAMES, i),
    createdDate: ago(i * 9 + 2),
    updatedDate: ago(i * 9),
    source: 'WEB',
    active: true,
    communicationChannel: i % 7 === 3 ? 'WHATSAPP' : 'WEB',
    isWaGroup: false,
  }));
}

/** The Symphony group chats — the SAME two groups the phones seed under `SYMPHONY_CHAT_SEED_GROUP=1`
 *  (`lib/group-chat-fixture.ts`), under mock ids so no request about them reaches the server. */
export const GROUP_CHAT_MOCK_IDS = ['mock-conversation-group-1', 'mock-conversation-group-2'] as const;
function groupChatRows(count: number): Record<string, unknown>[] {
  const groups = [
    { name: 'Launch crew', group: GROUP_SEED_LAUNCH, lastMessagePreview: GROUP_SEED_LAUNCH_PREVIEW },
    { name: '', group: GROUP_SEED_UNNAMED, lastMessagePreview: GROUP_SEED_UNNAMED_PREVIEW },
  ];
  return groups.slice(0, Math.min(count, 2)).map((g, i) => ({
    id: GROUP_CHAT_MOCK_IDS[i],
    name: g.name,
    createdDate: ago(i + 1),
    updatedDate: ago(i * 0.5),
    source: 'SCHEDULER',
    active: true,
    communicationChannel: 'WEB',
    isWaGroup: false,
    lastMessagePreview: g.lastMessagePreview,
    // "Launch crew" is pinned, so the wide rail (which lists pinned chats) shows a group row too.
    pinned: i === 0,
    group: g.group,
  }));
}

/** "Launch crew"'s transcript, already in the `Message` shape `/api/conversations/:id/messages` returns. */
function groupChatThread(): unknown[] {
  return GROUP_SEED_TRANSCRIPT.map(({ metadata, ...m }, i) => ({
    ...m,
    // Phase 5: the approval cards and the "can't help" notices, read the way the history route reads them.
    ...(parseGroupApproval(metadata) ? { groupApproval: parseGroupApproval(metadata) } : {}),
    ...(parseMaestroUnavailable(metadata) ? { maestroUnavailable: parseMaestroUnavailable(metadata) } : {}),
    // Phase 8: the typed Maestro mentions and the SYSTEM lines, read the way the history route reads them.
    ...(parseGroupMessageMeta(metadata) ? { groupMeta: parseGroupMessageMeta(metadata) } : {}),
    conversationId: GROUP_CHAT_MOCK_IDS[0],
    createdDate: new Date(Date.now() - (GROUP_SEED_TRANSCRIPT.length - i) * 60_000).toISOString(),
  }));
}

interface MockDetail {
  /** The api path, with the id captured. */
  match: RegExp;
  body: (id: string, ctx: MockContext) => unknown;
}

const MOCK_DETAILS: MockDetail[] = [
  // Liveness is decoration and its real route answers 200 even on failure, so "not live" is a true
  // answer for a task no agent is working on.
  { match: /^\/api\/task\/[^/]+\/liveness$/, body: () => ({ isLive: false, currentStep: null }) },
  {
    match: /^\/api\/task\/([^/]+)$/,
    body: (id, ctx) => ({
      task: {
        id,
        title: pick(TASK_TITLES, idIndex(id)),
        brief: 'Mock agent task.',
        subtitle: '',
        summary: 'Mock progress.',
        currentAction: `Working on "${pick(PLAN_STEPS, idIndex(id)).toLowerCase()}"`,
        status: TASK_STATUSES[idIndex(id) % TASK_STATUSES.length],
        agentId: mockId('agent', agentIndex(ctx, idIndex(id))),
        steps: mockPlanSteps(ctx),
        artifacts: [],
        workspaceId: workspaceRef(ctx, idIndex(id)).id,
        date: ago(1),
        createdDate: ago(4),
        // The same routine the row carries (see the `tasks` entity), so the detail's "From routine" line
        // matches the list it was opened from.
        ...(mockTaskRoutine(idIndex(id), ctx.count('routines')) ?? {}),
      },
    }),
  },
  // An empty thread: a mocked conversation row has no messages behind it, and inventing a transcript
  // would put words in the assistant's mouth.
  //
  // The ONE exception is `mock-conversation-qac-1`, which is a REPRODUCTION rather than invented
  // dialogue: the row order `questionAfterCards` exists to fix — an ask carrying `choices` followed by
  // own-row cards. It is the NEWEST row in the mocked conversation list ("Question after cards (demo)").
  // `?cards=` picks the trailing cards ("+"-joined: success · genome · atwork · gift), default
  // `success+genome`. Mirrors the two phones' SYMPHONY_CHAT_SEED_QUESTION_AFTER_CARDS launch hook.
  // A mocked group chat's own row (the chat surface reads it before its history): the same row the list serves.
  {
    match: /^\/api\/conversations\/(mock-conversation-group-\d+)$/,
    body: (id) => groupChatRows(2).find((row) => row.id === id) ?? null,
  },
  {
    match: /^\/api\/conversations\/([^/]+)\/messages$/,
    body: (id, ctx) => ({
      messages: id === QUESTION_AFTER_CARDS_CONVERSATION ? questionAfterCardsThread(ctx) : id === GROUP_CHAT_MOCK_IDS[0] ? groupChatThread() : [],
      turn: { pendingPredictionId: null, updatedDate: null },
      // The `chats-v1` window's own fields (the Chats page reads that contract); the legacy reader ignores them.
      olderCursor: null,
      readThroughToken: null,
      asOf: new Date().toISOString(),
      readState: { lastReadAt: null, lastReadCursor: null, hasUnreadMessages: false, hasUnreadNotifications: false, versions: { reader: null, messageHead: null, notifications: null } },
    }),
  },
  // `?dashboard=<workspace>&meta=1` asks whether a workspace HAS a dashboard. A faked one does not, and
  // `null` is the answer the home already handles by falling back to the composer landing.
  { match: /^\/api\/dashboard$/, body: () => ({ result: null }) },
];

/** The trailing number in a mock id (`mock-agent-task-2` → 1), so a detail matches the row it came from. */
function idIndex(id: string): number {
  const n = Number(/(\d+)$/.exec(id)?.[1] ?? 1);
  return Math.max(0, n - 1);
}

/**
 * Whether an entity survives the panel's search box. Matches its label, its hint and its key, so
 * "qr", "app" or the raw `mobileAppInstalled` all find the same row — the key is in there because it
 * is what the code says, and the panel is a developer tool.
 *
 * A matching PARENT keeps its sub-entities: hiding "Skills" the moment you search "agents" would
 * suggest agents have none. A matching CHILD stands on its own — it names itself well enough.
 */
export function matchesMockSearch(entity: MockEntity, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hits = (e: MockEntity) => `${e.label} ${e.hint ?? ''} ${e.key}`.toLowerCase().includes(needle);
  if (hits(entity)) return true;
  const parent = entity.within ? BY_KEY.get(entity.within) : undefined;
  return parent ? hits(parent) : false;
}

/** The entity that owns the endpoint carrying `key` — itself, or the parent it lives inside. */
export function ownerOf(key: MockEntityKey): MockEntity {
  const entity = BY_KEY.get(key) as MockEntity;
  return entity.within ? ownerOf(entity.within) : entity;
}

/** An owner plus everything carried inside its response — the set one endpoint answers for. */
function familyOf(owner: MockEntity): MockEntityKey[] {
  return [owner.key, ...MOCK_ENTITIES.filter((e) => e.within === owner.key).map((e) => e.key)];
}

/**
 * Everything that can change this endpoint's body: what it carries, plus any entity that declares it
 * `influences` this one. Faking the plan has to serve the credits route too — see `influences`.
 */
function answeredBy(owner: MockEntity): MockEntityKey[] {
  return [...familyOf(owner), ...MOCK_ENTITIES.filter((e) => e.influences?.includes(owner.key)).map((e) => e.key)];
}

// ─── stored state ────────────────────────────────────────────────────────────────────────────────

interface DevMockState {
  /** Entity → the count to serve. An absent key means "leave it to the server". */
  counts: Partial<Record<MockEntityKey, number>>;
  /** The real count last seen for an entity, so a sibling we are NOT faking stays truthful. */
  server: Partial<Record<MockEntityKey, number>>;
  /** A real workspace, so mocked children can hang off it when workspaces aren't faked. */
  sampleWorkspace?: { id: string; name: string; icon: string };
  /**
   * Two REAL AgentPlan ids out of the live catalog, recorded when the panel last probed: the top
   * ("max") tier and any cheaper paid tier. A mocked subscription points at one of these rather
   * than at an invented id, because every consumer resolves the plan THROUGH the catalog —
   * `activePlanIsMaxPlus` matches `key === 'max'`, the card's title is the catalog's name key, and
   * the top-up pack is the catalog option's own. An invented id resolves to none of that and the
   * card would render "Your plan" with no top-up, which is not a tier anyone can ship.
   */
  samplePlanIds?: { paid: string | null; maxPlus: string | null };
}

const EMPTY: DevMockState = { counts: {}, server: {} };

function storage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined; // private mode / storage disabled
  }
}

function readState(): DevMockState {
  const raw = storage()?.getItem(STORAGE_KEY);
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw) as Partial<DevMockState>;
    // Every field is carried through explicitly — a new one missing here is dropped on read while
    // `writeState` keeps persisting it, which reads as "the panel never probed" and silently falls
    // back (that is exactly how `samplePlanIds` first shipped broken).
    return {
      counts: parsed.counts ?? {},
      server: parsed.server ?? {},
      sampleWorkspace: parsed.sampleWorkspace,
      samplePlanIds: parsed.samplePlanIds,
    };
  } catch {
    return EMPTY;
  }
}

function writeState(next: DevMockState): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota — the panel still holds the draft for this session */
  }
}

/** The counts currently being faked. */
export function devMockCounts(): Partial<Record<MockEntityKey, number>> {
  return devMockAvailable() ? readState().counts : {};
}

/** How many entities are faked right now — what the dev menu labels itself with. */
export function devMockCount(): number {
  return Object.keys(devMockCounts()).length;
}

/** Replace the faked counts wholesale (the panel's Apply). Drop a key to hand it back to the server. */
export function applyDevMockCounts(counts: Partial<Record<MockEntityKey, number>>): void {
  if (!devMockAvailable()) return;
  const clean: Partial<Record<MockEntityKey, number>> = {};
  for (const [key, value] of Object.entries(counts)) {
    if (typeof value === 'number' && Number.isFinite(value) && BY_KEY.has(key as MockEntityKey)) {
      clean[key as MockEntityKey] = Math.max(0, Math.floor(value));
    }
  }
  writeState({ ...readState(), counts: clean });
}

/**
 * Whether a workspace's dashboard should read as "nothing there yet" — the Growth Insights tab's empty
 * state. Consulted by `lib/dashboard.ts` rather than served as a body: the existence probe reads the
 * DashboardRenderHandler (a gateway route, not `/api/`), so `devMockResponse` never sees it.
 *
 * Two ways to be empty, and the second is not optional: a SYNTHETIC workspace exists in this browser
 * alone, so no dashboard can ever be rendered for it and the probe would otherwise fail rather than
 * answer — leaving the tab on its loader forever.
 *
 * The home/catch-up dashboard (no `dashboard` in the target) is deliberately untouched: this row is
 * scoped per workspace, and the home's empty state is its own onboarding surface. Every OTHER target
 * is a dashboard addressed by slug, and a target carries no way to tell a workspace's from a pack's —
 * so those read empty too. The row's hint says so rather than claiming a precision it hasn't got.
 */
export function devMockDashboardEmpty(target: { dashboard?: string }): boolean {
  if (!devMockAvailable()) return false;
  // No slug is the HOME dashboard, which has its own row — a workspace row at 0 never empties the home.
  if (!target.dashboard) return readState().counts.homeDashboard === 0;
  if (isMockId(target.dashboard)) return true;
  return readState().counts.workspaceDashboard === 0;
}

/** Throw every local change away — back to the server's data, everywhere. */
export function clearDevMockData(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

// ─── serving ─────────────────────────────────────────────────────────────────────────────────────

/**
 * While true, `devMockResponse` stands down so a caller can read the SERVER's data (the panel
 * counting what really exists). Dev-only and short-lived; a UI request landing inside the window
 * just gets real data, which is harmless.
 */
let serveServerOnly = false;

function contextFor(state: DevMockState, params: URLSearchParams): MockContext {
  return {
    // The faked number, else the real one we last counted, else the entity's fallback.
    count: (key) => state.counts[key] ?? state.server[key] ?? (BY_KEY.get(key) as MockEntity).fallback,
    faked: (key) => state.counts[key] !== undefined,
    sampleWorkspace: state.sampleWorkspace,
    params,
  };
}

/**
 * `<mount>/api/foo?x=1` → `{ api: '/api/foo', params }`. Any non-`/api/` URL isn't ours.
 *
 * The mount prefix is sliced off rather than matched, so this reads whatever `API_BASE` resolved for
 * the active surface — the registry's endpoints stay mount-free.
 */
function splitUrl(input: string): { api: string; params: URLSearchParams } | undefined {
  const [path, query = ''] = input.split('?');
  const at = path.indexOf('/api/');
  if (at < 0) return undefined;
  return { api: path.slice(at), params: new URLSearchParams(query) };
}

const json = (body: unknown, status = 200): Response =>
  new Response(JSON.stringify(body), { status, headers: { 'content-type': 'application/json' } });

/** Whether this request names a synthetic entity — anywhere in the path, or as any query value. */
function referencesMockId(url: { api: string; params: URLSearchParams }): boolean {
  if (url.api.split('/').some(isMockId)) return true;
  for (const value of url.params.values()) if (isMockId(value)) return true;
  return false;
}

/**
 * A request naming a synthetic entity, answered here whatever it asks for.
 *
 * The invariant is that it must not reach the server: ids are GUIDs there, so the request is refused
 * rather than answered, and the failure lands in production logs long after whoever was mocking closed
 * the tab. When nothing can answer it honestly the reply is a real 404 plus a LOUD console error — the
 * gap belongs to whoever is looking at this screen, and a fabricated 200 of unknown shape would hide it.
 */
function serveSynthetic(url: { api: string; params: URLSearchParams }, state: DevMockState): Response {
  const ctx = contextFor(state, url.params);

  const owner = MOCK_ENTITIES.find((e) => e.endpoint === url.api);
  if (owner?.body) {
    console.info(`${LOG} ${url.api} served locally (synthetic id)`);
    return json(owner.body(ctx));
  }

  for (const detail of MOCK_DETAILS) {
    const hit = detail.match.exec(url.api);
    if (hit) {
      console.info(`${LOG} ${url.api} served locally (synthetic id)`);
      return json(detail.body(hit[1] ?? '', ctx));
    }
  }

  console.error(
    `${LOG} ${url.api} names a mocked entity and nothing here can answer it, so it was NOT sent to the` +
      ` server (the id is not a GUID there). Add a MOCK_DETAILS entry for this route in lib/dev-mock-data.ts.`,
  );
  return json({ error: 'not_found' }, 404);
}

/**
 * The interception point, called by `apiFetch` on every same-origin data call in a dev build. Returns a
 * synthesized `Response` when this URL's entity is being faked, else `undefined` so the real request
 * goes out.
 *
 * Reads are faked only when something is faked; a request naming a SYNTHETIC id is answered here
 * regardless, writes included. A write against an entity that exists in one browser cannot mean
 * anything, and the counts may already have been cleared while such a URL is still on screen.
 */
export function devMockResponse(input: string, init: RequestInit = {}): Response | undefined {
  if (!devMockAvailable() || serveServerOnly) return undefined;

  const url = splitUrl(input);
  if (!url) return undefined;

  const state = readState();
  if (referencesMockId(url)) return serveSynthetic(url, state);
  // A group action (Phase 3b) carries its ids in the BODY: one naming a mocked chat or a `mock-invite-<n>` is
  // answered by the fixture, never sent (the ids are GUIDs there).
  const groupAction = /^\/api\/group-chat\/([a-z-]+)$/.exec(url.api);
  if (groupAction && typeof init.body === 'string') {
    let body: Record<string, unknown> = {};
    try {
      body = JSON.parse(init.body) as Record<string, unknown>;
    } catch {
      // not JSON: not ours
    }
    if (Object.values(body).some((v) => typeof v === 'string' && isMockId(v))) {
      console.info(`${LOG} ${url.api} served locally (group chat fixture)`);
      return json(groupChatMockAnswer(groupAction[1], body));
    }
  }

  if ((init.method ?? 'GET').toUpperCase() !== 'GET') return undefined;
  if (Object.keys(state.counts).length === 0) return undefined;
  // The route's own entity, else the entity that MIRRORS it — a second route reporting the same fact
  // in another shape, which is answered from that row's count with the mirror's own body.
  const owner = MOCK_ENTITIES.find((e) => e.endpoint === url.api);
  const mirrored = owner ? undefined : findMirror(url.api);
  const source = owner ?? mirrored?.entity;
  if (!source) return undefined;
  const faked = answeredBy(source).filter((key) => state.counts[key] !== undefined);
  if (faked.length === 0) return undefined;

  console.info(`${LOG} ${url.api} served locally (${faked.join(', ')})`);
  const build = mirrored?.body ?? (source.body as (ctx: MockContext) => unknown);
  return json(build(contextFor(state, url.params)));
}

/** The entity whose `mirrors` claims this path, with the body to answer it. */
function findMirror(api: string): { entity: MockEntity; body: (ctx: MockContext) => unknown } | undefined {
  for (const entity of MOCK_ENTITIES) {
    const mirror = entity.mirrors?.find((m) => m.endpoint === api);
    if (mirror) return { entity, body: mirror.body };
  }
  return undefined;
}

// ─── counting what really exists ─────────────────────────────────────────────────────────────────

/** What the panel shows per row: how many the account really has, or `undefined` if the probe failed. */
export type ServerCounts = Partial<Record<MockEntityKey, number>>;

/**
 * Ask the server how many of everything there actually is — the "3 agents" on each panel row.
 *
 * `fetcher` is injected (the panel passes `apiFetch`) so this module never imports the client it is
 * hooked into. Workspaces are probed first: their real ids are what the per-workspace endpoints are
 * asked about, and the first one is remembered as the sample mocked children hang off.
 */
export async function probeServerCounts(fetcher: (url: string) => Promise<Response>, apiBase: string): Promise<ServerCounts> {
  if (!devMockAvailable()) return {};
  const counts: ServerCounts = {};
  const state = readState();
  let sampleWorkspace = state.sampleWorkspace;
  let samplePlanIds = state.samplePlanIds;

  const read = async (entity: MockEntity, workspaceId?: string): Promise<any> => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const query = entity.scope === 'workspace' ? `?workspaceId=${encodeURIComponent(workspaceId ?? '')}` : '';
    const res = await fetcher(`${apiBase}${entity.endpoint}${query}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const record = (owner: MockEntity, body: unknown): void => {
    for (const key of familyOf(owner)) {
      const value = (BY_KEY.get(key) as MockEntity).countIn?.(body);
      if (typeof value === 'number' && Number.isFinite(value)) counts[key] = value;
    }
  };

  serveServerOnly = true;
  try {
    const workspaces = MOCK_ENTITIES.find((e) => e.key === 'workspaces') as MockEntity;
    try {
      const body = await read(workspaces);
      record(workspaces, body);
      const first = body?.workspaces?.[0];
      if (first?.id) sampleWorkspace = { id: first.id, name: first.name ?? '', icon: first.icon ?? '' };
    } catch (err) {
      console.warn(`${LOG} could not count workspaces`, err);
    }

    // The plan catalog, for the same reason the sample workspace is recorded: a mocked subscription
    // has to name a plan the CATALOG knows, or the card falls back to "Your plan" with no top-up.
    // Read before the batch below, because the `plan` row's `countIn` classifies Max+ against it.
    try {
      const res = await fetcher(`${apiBase}/api/plans`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const catalog = ((await res.json())?.plans ?? []) as {
        key?: string;
        options?: { agentPlanId?: string; topup?: unknown; topTier?: boolean }[];
      }[];
      // Prefer the Max+ option (marked `topTier`, or carrying the top-up pack): Max and Max+ share
      // the max card, so picking a sibling would fake Max+ as an ordinary paid plan.
      const idOf = (match: (key: string) => boolean) => {
        const options = catalog.find((p) => !!p.key && match(p.key) && !!p.options?.length)?.options ?? [];
        return (options.find((o) => o.topTier) ?? options.find((o) => o.topup) ?? options[0])?.agentPlanId ?? null;
      };
      samplePlanIds = { maxPlus: idOf((k) => k === 'max'), paid: idOf((k) => k !== 'max' && k !== 'free') };
    } catch (err) {
      console.warn(`${LOG} could not read the plan catalog — the plan row will fake a tier the catalog can't name`, err);
    }

    const rest = MOCK_ENTITIES.filter((e) => e.endpoint && e.key !== 'workspaces');
    await Promise.all(
      rest.map(async (entity) => {
        try {
          record(entity, await read(entity, sampleWorkspace?.id));
        } catch (err) {
          console.warn(`${LOG} could not count ${entity.key}`, err);
        }
      }),
    );
  } finally {
    serveServerOnly = false;
  }

  // Remember them: after the reload that applies a fake, a sibling we are NOT faking still needs a
  // truthful number (e.g. faking agents alone must not change how many skills the team has).
  writeState({ ...readState(), server: { ...readState().server, ...counts }, sampleWorkspace, samplePlanIds });
  return counts;
}

/** The counts a previous probe recorded — what the panel shows before its own probe lands. */
export function lastKnownServerCounts(): ServerCounts {
  return devMockAvailable() ? readState().server : {};
}
